// Swiss-pairing generator for 1v1 quals. Run round-by-round; pairs each team
// with a similarly-scored opponent it has not yet played. Ops invokes this
// once a round completes; the next round's matches drop into matches1v1.
//
// Algorithm — score-group pairing (a simplified Dutch / chess Swiss):
//   1. Sort eligible teams by current rank (RP / H2H / cumulative / median
//      Buchholz — the chain `computeStandings1v1` uses).
//   2. If the pool size is odd, assign a bye to the lowest-ranked team that
//      has not yet received one. The bye produces a synthetic Match with
//      `bye: true` (red = the byed team, blue empty); standings credit it
//      as a free win (3 RP, 1 W) with self-pair Buchholz. Falls back to a
//      repeat bye only if every team has already had one.
//   3. Partition the remaining pool into score groups (consecutive runs of
//      teams sharing the same RP). Process groups top-down:
//      a. Each downfloated team from a higher group plays the bottom-most
//         (lowest-ranked) unplayed team in the current group — the standard
//         Dutch downfloat. Falls through to a rematch if every team in the
//         destination group has already played the float.
//      b. If the remaining group is odd-sized, downfloat the lowest-ranked
//         team into the next group. Total pool parity (forced even by the
//         bye extraction) guarantees this terminates with no leftover team
//         in the last group.
//      c. Top-half / bottom-half pairing on the even remainder: top[i] vs
//         bot[i] for i=0..N-1, with a one-position swap of bot entries to
//         avoid rematches. This keeps the strongest matchups for later
//         rounds — in round 2 of a 28-team field, e.g., the rank-1 team
//         plays the rank-8 team rather than rank-2.
//   4. Higher-ranked team (smaller rank) takes the red side. Deterministic
//      so a re-run of the same inputs produces the same pairings.
//
// Time placement: round-N matches start 30 min after the last R(N-1) match
// finishes (assumed 20-min slots). Ops can swap/reschedule individual matches
// once the schedule editor lands. If times can't be inferred (no prior round
// in the data), fall back to "TBD" — the row still renders but won't sort.
//
// IDs: round-N occupies E((N-1)*14+1) … E(N*14). Round 1 already exists as
// E1-E14 in the seed data; this function only generates rounds 2..totalRounds.
// When a bye exists in a round, it takes the LAST id in that range so the
// pairings keep the leading slots (e.g. round 2 with 27 teams: E15-E27 are
// pairings, E28 is the bye).
//
// Limitations:
//   - Greedy, not Edmonds-blossom. The local rematch-avoidance swap inside
//     a group covers virtually all real-world cases; pathological pairings
//     (where the only resolution is to swap a top-half entry, not just bot
//     entries) fall through to a rematch. With 28 teams over 6 rounds the
//     search space is wide enough that this isn't observed in practice.
//   - Doesn't enforce alternating colors (red/blue) across a team's rounds —
//     not meaningful in this game format.
//   - Does NOT mutate state. Caller appends the result to matches1v1.

import type { Match, RoundResult, Team, TeamId } from '../types';
import { computeStandings1v1 } from './standings';
import {
  DEFAULT_ARC_SCHEDULE, findBlockFor, formatScheduledAt, nextBlockAfter,
  packMatchesIntoBlocks, resolveBlocks,
} from './schedule';

/**
 * Single source of truth for the number of Swiss rounds in a 1v1 qualifier.
 * Imported by Match Control (round-cap UI), the Swiss generator, the public
 * standings page, and the bracket page so the value is consistent across
 * call sites. With 28 teams, 6 rounds gives every team enough comparisons
 * for a stable ranking before the top-8 cut.
 */
export const SWISS_TOTAL_ROUNDS = 6;

/**
 * Total *real* (non-bye) 1v1 matches across the whole Swiss, for a field of
 * `teamCount` teams: floor(N/2) pairings per round × SWISS_TOTAL_ROUNDS. Used by
 * the public schedule header to report an accurate "upcoming" count even before
 * later rounds are generated (future rounds don't exist as Match rows yet).
 */
export function totalSwissMatches(teamCount: number): number {
  return SWISS_TOTAL_ROUNDS * Math.floor(teamCount / 2);
}

export interface SwissGenerateResult {
  matches: Match[];
  /** Rounds played by every team prior to this generate. */
  fromRound: number;
  /** Round number of the matches we produced. */
  toRound: number;
  /** The team that received a bye this round, if any. */
  byeTeam?: TeamId;
}

const PLAYOFF_PREFIX = /^E_/;
const QUAL_PREFIX = /^E\d+$/;

/**
 * Generate the next Swiss round for 1v1 quals. Returns matches to append;
 * caller is responsible for the actual state update.
 *
 * @param matches  All current 1v1 matches (quals + playoffs — we filter).
 * @param results  Recorded round results (to determine W/L/T history).
 * @param teams    Full team roster.
 * @param totalRounds  Cap (defaults to SWISS_TOTAL_ROUNDS). If we're already
 *                    at totalRounds the function returns an empty array.
 */
export function generateNextSwissRound(
  matches: Match[],
  results: Record<string, RoundResult[]>,
  teams: Team[],
  totalRounds: number = SWISS_TOTAL_ROUNDS,
): SwissGenerateResult {
  // Only consider 1v1 quals when computing history; playoffs use E_-prefixed ids.
  const quals = matches.filter((m) => QUAL_PREFIX.test(m.id) && !PLAYOFF_PREFIX.test(m.id));

  // Standings deriver tells us the current round and ranked teams.
  const standings = computeStandings1v1(quals, results, teams, totalRounds);
  const fromRound = standings.round;
  const toRound = fromRound + 1;
  if (fromRound >= totalRounds) {
    return { matches: [], fromRound, toRound: fromRound };
  }

  // Build history maps from quals only (not playoffs):
  //   played    — pair of team_ids that have already faced each other
  //   hasBye    — teams that already received a bye this tournament
  const played = new Map<TeamId, Set<TeamId>>();
  const hasBye = new Set<TeamId>();
  for (const m of quals) {
    if (m.bye === true && m.red) {
      hasBye.add(m.red);
      continue;
    }
    if (!m.red || !m.blue) continue;
    if (!played.has(m.red))  played.set(m.red,  new Set());
    if (!played.has(m.blue)) played.set(m.blue, new Set());
    played.get(m.red)!.add(m.blue);
    played.get(m.blue)!.add(m.red);
  }

  // Working pool — standings rows in rank order. Mutated as we pull teams.
  let pool = standings.rows.slice();

  // Odd-roster bye assignment. Walk from the bottom up to find the lowest-
  // ranked team that has not yet had a bye. Fallback (every team had one
  // already → very rare with ≤6 rounds and N≥7 odd) takes the bottom team.
  let byeTeam: TeamId | undefined;
  if (pool.length % 2 === 1) {
    let pickIndex = -1;
    for (let i = pool.length - 1; i >= 0; i--) {
      if (!hasBye.has(pool[i].team)) {
        pickIndex = i;
        break;
      }
    }
    if (pickIndex === -1) pickIndex = pool.length - 1;
    byeTeam = pool[pickIndex].team;
    pool = pool.slice(0, pickIndex).concat(pool.slice(pickIndex + 1));
  }

  // Pairing. Backtracking Dutch pairer (see pairNoRematch): pairs the rank-
  // sorted, even `pool` so no two teams meet twice WHENEVER a rematch-free
  // pairing of the whole pool exists — it backtracks across the entire field,
  // not one adjacent bracket, so a small all-played score group can't force a
  // spurious rematch (which the old greedy single-hop downfloat could). The
  // candidate order follows the Dutch fold / bottom-most-unplayed downfloat, so
  // the first solution matches standard chess-Swiss pairings and it only searches
  // deeper when the natural pairing would repeat. If the field is genuinely
  // exhausted (every remaining opponent already played), it falls back to a
  // minimal rematch so the schedule never stalls.
  const pairs = pairNoRematch(pool, played, { n: 0 }) ?? pairAllowingRematch(pool, played);

  // Time placement. Pack new matches into the schedule starting AFTER the
  // latest existing qual ends (= scheduledAt + duration). Falls back to TBD
  // display strings if no scheduledAt exists in the existing data (legacy).
  // Bye matches don't occupy a real time slot but we still pack them through
  // the same loop so they get a consistent (if conceptually ignored)
  // scheduledAt — the UI may surface "BYE — <team>" at that time.
  const blocks = resolveBlocks(DEFAULT_ARC_SCHEDULE);
  const duration = DEFAULT_ARC_SCHEDULE.matchDurationMinutes.qual_1v1 ?? 15;
  // Round-gating: a new Swiss round starts at the NEXT block after the block
  // holding the latest REAL (non-bye) qual — so each round begins on a fresh
  // block, giving teams the inter-round gap before the next pairings drop. A
  // round that overflows its starting block flows into subsequent blocks via
  // the packer. Byes are excluded from this anchor (they hold no real slot).
  const latestRealAt = quals
    .filter((m) => !m.bye)
    .map((m) => m.scheduledAt)
    .filter((s): s is string => !!s)
    .sort()
    .pop();
  const lastBlock = latestRealAt ? findBlockFor(latestRealAt, blocks) : null;
  const startBlock = lastBlock ? nextBlockAfter(lastBlock, blocks) : null;
  // Add N minutes to a scheduled-at ISO ("YYYY-MM-DDTHH:MM:00Z", wall-clock-as-UTC).
  const addMinutesIso = (iso: string, minutes: number): string => {
    const d = new Date(iso);
    d.setUTCMinutes(d.getUTCMinutes() + minutes);
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}T${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:00Z`;
  };
  // Prefer a FRESH next block (the inter-round gap). If there's none — official
  // delays / overflow pushed round N-1's last match into the final block, or the
  // schedule is full — start right after that match so the round still schedules.
  const fromIso = startBlock?.starts ?? (latestRealAt ? addMinutesIso(latestRealAt, duration) : undefined);

  // ID scheme: round N starts at E((N-1)*14 + 1). Pairings get the leading
  // ids; bye (if any) gets the next id after the last pairing. Pack ONLY the
  // real pairings — the bye doesn't consume a slot; it shares the round's last
  // real slot (below) so it still renders in the schedule without shifting
  // anything or pushing the next round.
  // Matches per round = this round's pairings + a bye if the roster is odd
  // (= ceil(teams/2)). Constant across rounds for a fixed field, so each round
  // gets a contiguous id block: 24 teams → 12/round (R1 E1-E12, R2 E13-E24);
  // 27 teams → 14/round (R1 E1-E14, R2 E15-E28). The old hardcoded 14 only fit a
  // 27-28-team field and left gaps (E13/E14) for anything smaller.
  const matchesPerRound = pairs.length + (byeTeam ? 1 : 0);
  const idStart = (toRound - 1) * matchesPerRound + 1;
  const items = pairs.map((_, i) => ({ id: `E${idStart + i}`, durationMin: duration }));
  const { assigned } = fromIso
    ? packMatchesIntoBlocks(items, blocks, { from: fromIso })
    : { assigned: [] as Array<{ id: string; scheduledAt: string; blockId: string }> };
  const slotById = new Map(assigned.map((a) => [a.id, a.scheduledAt]));
  const byeAt = assigned.length ? assigned[assigned.length - 1].scheduledAt : undefined;

  const newMatches: Match[] = pairs.map((p, i) => {
    const id = `E${idStart + i}`;
    const at = slotById.get(id);
    return {
      id,
      time: at ? formatScheduledAt(at) : 'TBD',
      scheduledAt: at,
      red: p.red,
      blue: p.blue,
      redS: 'not_tracked',
      blueS: 'not_tracked',
    };
  });

  if (byeTeam) {
    const id = `E${idStart + pairs.length}`;
    // A bye lands as `completed` immediately on both sides — there's no
    // physical match for inspection / advancement to gate on. It shares the
    // round's last real slot (byeAt) so it shows in the schedule alongside that
    // match without consuming a slot of its own.
    newMatches.push({
      id,
      time: byeAt ? formatScheduledAt(byeAt) : 'TBD',
      scheduledAt: byeAt,
      red: byeTeam,
      blue: '',
      redS: 'completed',
      blueS: 'completed',
      bye: true,
    });
  }

  return { matches: newMatches, fromRound, toRound, byeTeam };
}

// ── Backtracking Swiss pairer ───────────────────────────────────────────────
// Guarantees no rematch whenever a rematch-free perfect pairing of the whole
// pool exists (unlike the old greedy pass, which only floated one hop and would
// accept a rematch if that adjacent group was exhausted). Only the `team` +
// `rp` fields are needed, so it's typed structurally.

type PairRow = { team: TeamId; rp: number };

// Ordered candidate opponents for `top` (the highest-ranked unpaired team),
// best first. `rest` is the remaining pool in rank order.
//   - Resident (top still has same-RP peers below it): Dutch fold — top plays
//     the top of its score group's bottom half (peers[⌊g/2⌋−1]), widening out
//     from there, then downfloating to lower scores.
//   - Downfloater (alone at its RP): closest lower score group first, and the
//     bottom-most team within it first (the standard downfloat target).
function swissCandidateOrder(top: PairRow, rest: PairRow[]): PairRow[] {
  const peers = rest.filter((r) => r.rp === top.rp);
  const lower = rest.filter((r) => r.rp !== top.rp);
  if (peers.length === 0) {
    const byScore = new Map<number, PairRow[]>();
    for (const r of lower) {
      const arr = byScore.get(r.rp) ?? [];
      arr.push(r);
      byScore.set(r.rp, arr);
    }
    return [...byScore.keys()]
      .sort((a, b) => b - a)                              // closest (highest) score first
      .flatMap((s) => byScore.get(s)!.slice().reverse()); // bottom-most within each
  }
  const g = peers.length + 1;                              // group size incl. top
  const foldIdx = Math.floor(g / 2) - 1;                   // top's fold partner within peers
  const out: PairRow[] = [];
  for (let d = 0; d < peers.length; d++) {
    if (d === 0) { out.push(peers[foldIdx]); continue; }   // fold partner first
    if (foldIdx + d < peers.length) out.push(peers[foldIdx + d]);
    if (foldIdx - d >= 0) out.push(peers[foldIdx - d]);    // then widen outward
  }
  return out.concat(lower);                                // then downfloat
}

// Pair the top team with its best legal (non-rematch) opponent, recurse, and
// undo on a dead-end. Returns null if no rematch-free perfect pairing exists.
// `steps` bounds worst-case backtracking (never approached at real field sizes,
// but prevents a pathological hang).
function pairNoRematch(
  pool: PairRow[],
  played: Map<TeamId, Set<TeamId>>,
  steps: { n: number },
): { red: TeamId; blue: TeamId }[] | null {
  if (pool.length === 0) return [];
  if (++steps.n > 200_000) return null;
  const [top, ...rest] = pool;
  for (const opp of swissCandidateOrder(top, rest)) {
    if (played.get(top.team)?.has(opp.team)) continue;     // never a rematch
    const sub = pairNoRematch(rest.filter((r) => r.team !== opp.team), played, steps);
    if (sub) return [{ red: top.team, blue: opp.team }, ...sub];
  }
  return null;
}

// Fallback for an exhausted field (every remaining opponent already played, or
// the backtracking budget was hit): greedy best-first with rematches allowed —
// a perfect matching always exists on an even complete graph, so no backtracking
// is needed. Prefers a non-rematch at each step to minimize repeats.
function pairAllowingRematch(
  pool: PairRow[],
  played: Map<TeamId, Set<TeamId>>,
): { red: TeamId; blue: TeamId }[] {
  if (pool.length === 0) return [];
  const [top, ...rest] = pool;
  const ordered = swissCandidateOrder(top, rest);
  const opp = ordered.find((o) => !played.get(top.team)?.has(o.team)) ?? ordered[0];
  return [{ red: top.team, blue: opp.team }, ...pairAllowingRematch(rest.filter((r) => r.team !== opp.team), played)];
}
